import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import en from "./en";
import de from "./de";
import fr from "./fr";
import { LanguageCode, LanguageCodeArray } from "@juhuu/sdk-ts";

const languages = {
  en,
  de,
  fr,
};

type TranslationDictionary = typeof en | typeof de | typeof fr;

interface TranslationContextProps {
  dictionary: TranslationDictionary;
  selectedLanguage: LanguageCode;
  browserLanguage: LanguageCode | null;
  flagEmoji: string | null;
  setLanguage: (lang: LanguageCode) => void;
}

const defaultLang: LanguageCode = "en";

export const TranslationContext = createContext<TranslationContextProps>({
  dictionary: languages[defaultLang],
  selectedLanguage: defaultLang,
  browserLanguage: null,
  flagEmoji: null,
  setLanguage: () => {},
});

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedLanguage, setSelectedLanguage] =
    useState<LanguageCode>(defaultLang);
  const [browserLanguage, setBrowserLanguage] = useState<LanguageCode | null>(
    null
  );
  const [flagEmoji, setFlagEmoji] = useState<string | null>(null);

  const flagMap = {
    en: "🇬🇧", // or "🇺🇸" for US English
    de: "🇩🇪",
    fr: "🇫🇷",
    es: "🇪🇸",
    it: "🇮🇹",
    cs: "🇨🇿",
    pl: "🇵🇱",
    nl: "🇳🇱",
    sv: "🇸🇪",
    no: "🇳🇴",
    da: "🇩🇰",
    hu: "🇭🇺",
    et: "🇪🇪",
    hr: "🇭🇷",
  };

  useEffect(() => {
    const storedLanguage = localStorage.getItem(
      "language"
    ) as LanguageCode | null;
    if (storedLanguage && LanguageCodeArray.includes(storedLanguage)) {
      setSelectedLanguage(storedLanguage);
    }

    const browserLanguage = navigator.language
      .slice(0, 2)
      .toLowerCase() as LanguageCode;
    if (LanguageCodeArray.includes(browserLanguage)) {
      setBrowserLanguage(browserLanguage);
      setFlagEmoji(flagMap[browserLanguage] || null);
    }
  }, []);

  const setLanguage = useCallback((lang: LanguageCode) => {
    localStorage.setItem("language", lang);
    setSelectedLanguage(lang);
  }, []);

  return (
    <TranslationContext.Provider
      value={{
        dictionary:
          languages[selectedLanguage as keyof typeof languages] || languages.en,
        selectedLanguage,
        browserLanguage,
        flagEmoji,
        setLanguage,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

type TranslationVars = Record<string, string | number | boolean>;

export function useTranslation() {
  const {
    dictionary,
    selectedLanguage,
    browserLanguage,
    flagEmoji,
    setLanguage,
  } = useContext(TranslationContext);

  const t = (key: string, vars?: TranslationVars): string => {
    let result =
      key
        .split(".")
        .reduce<any>((obj, k) => (obj ? obj[k] : undefined), dictionary) ?? key;

    if (typeof result === "string" && vars) {
      Object.entries(vars).forEach(([k, v]) => {
        result = result.replace(new RegExp(`{{\\s*${k}\\s*}}`, "g"), String(v));
      });
    }

    return result;
  };

  return {
    t,
    selectedLanguage,
    browserLanguage,
    flagEmoji,
    setLanguage,
  };
}
